import React, {
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ArrayParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import {
  VulnerabilityConnectionInput,
  VulnerabilityFilters,
  VulnerabilityRelationshipReport,
  VulnerabilityRelationshipReportItem,
} from '@totem/components/vulnerabilities/overviewSummary/types';
import {
  addVulnerabilityFilters,
  buildVulnerabilityFilters,
} from '@totem/components/vulnerabilities/report/utilities';
import VulnerabilityReportContext from '@totem/components/vulnerabilities/report/vulnerabilityReportContext';
import { Params } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';
import { omitNilOrEmpty } from '@totem/utilities/objectUtilities';
type Props = {
  children?: ReactNode;
  defaultFilters?: VulnerabilityFilters;
  staticFilters?: VulnerabilityFilters;
};
const VulnerabilityReportContainer = ({
  children,
  defaultFilters,
  staticFilters,
}: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const controlSystems = urlParams.get('cs');
  const buildings = urlParams.get('b');
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(true);
  const [data, setData] = useState<VulnerabilityRelationshipReport>(null);
  const latestRequestId = useRef(0);
  const [action, setAction] = useState<string>('');
  const [actionItem, setActionItem] =
    useState<VulnerabilityRelationshipReportItem>(null);
  const [reportInput, updateReportInput] = useQueryParams({
    pageSize: withDefault(NumberParam, 10),
    page: withDefault(NumberParam, 1),
    sortField: withDefault(StringParam, 'sources.vulnerabilities.probability'),
    sortDirection: withDefault(StringParam, '1'),
    regionId: withDefault(
      ArrayParam,
      isNotNull(defaultFilters) && isNotNull(defaultFilters.regionId)
        ? defaultFilters.regionId
        : [],
    ),
    buildingId: withDefault(
      ArrayParam,
      isNotNull(defaultFilters) && isNotNull(defaultFilters.buildingId)
        ? defaultFilters.buildingId
        : [],
    ),
    controlSystemId: withDefault(
      ArrayParam,
      isNotNull(defaultFilters) && isNotNull(defaultFilters.controlSystemId)
        ? defaultFilters.controlSystemId
        : [],
    ),
    ownerId: withDefault(
      ArrayParam,
      isNotNull(defaultFilters) && isNotNull(defaultFilters.ownerId)
        ? defaultFilters.ownerId
        : [],
    ),
    ownerType: withDefault(
      ArrayParam,
      isNotNull(defaultFilters) && isNotNull(defaultFilters.ownerType)
        ? defaultFilters.ownerType
        : [],
    ),
    probability: withDefault(
      ArrayParam,
      isNotNull(defaultFilters) && isNotNull(defaultFilters.probability)
        ? defaultFilters.probability
        : [],
    ),
    status: withDefault(
      ArrayParam,
      isNotNull(defaultFilters) && isNotNull(defaultFilters.status)
        ? defaultFilters.status
        : [],
    ),
  });

  const bOnly = buildings === 't';
  const csOnly = controlSystems === 't';

  useEffect(() => {
    setRefresh(true);
  }, [userProfile.organization.id]);
  const setReportInput = (updated: Partial<VulnerabilityConnectionInput>) => {
    updateReportInput(omitNilOrEmpty({ ...updated }), 'replace');

    setRefresh(true);
  };
  useEffect(() => {
    if (!refresh) {
      return;
    }
    setRefresh(false);

    setLoading(true);
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;
    const params: Params = {
      paging: {
        page: reportInput.page,
        pageSize: reportInput.pageSize,
      },
      sort: {
        field: reportInput.sortField,
        direction: +reportInput.sortDirection,
      },
      filters: buildVulnerabilityFilters(reportInput),
    };
    params.filters = addVulnerabilityFilters(params.filters, staticFilters);
    if (
      bOnly &&
      (reportInput.buildingId === null || reportInput.buildingId.length === 0)
    ) {
      params.filters.push({
        field: 'buildingId',
        operator: 'ne',
        values: [EMPTY_ID],
      });
    }
    if (
      csOnly &&
      (reportInput.controlSystemId === null ||
        reportInput.controlSystemId.length === 0)
    ) {
      params.filters.push({
        field: 'controlSystemId',
        operator: 'ne',
        values: [EMPTY_ID],
      });
    }
    fetch(`${DEVICES_ENDPOINT}/vulnerabilities/report`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(params),
    })
      .then((res) => res.json())
      .then((result: VulnerabilityRelationshipReport) => {
        if (requestId !== latestRequestId.current) {
          return;
        }

        setData(result);

        setLoading(false);
      });
  }, [refresh, getApiToken]);
  const handleAction = (
    newAction: string,
    newActionItem: VulnerabilityRelationshipReportItem,
  ) => {
    switch (newAction) {
      case 'refresh':
        setRefresh(true);
        break;
      default:
        setAction(newAction);

        setActionItem(newActionItem);
    }
  };
  return (
    <VulnerabilityReportContext.Provider
      value={{
        data,
        loading,
        action,
        actionItem,
        onAction: handleAction,
        input: reportInput,
        setInput: setReportInput,
      }}
    >
      {children}
    </VulnerabilityReportContext.Provider>
  );
};
export default VulnerabilityReportContainer;
